import { TFunction } from 'i18next';
import { QuoteError } from '~/lib/api/smartsend/types';
import { sanitiseKey } from '~/lib/utils/translation';
import {
  ServiceIneligibilityMessage,
  ServiceIneligibilityResolutions,
  ServiceIneligibilitySubCode
} from './ServiceIneligibility';

/**
 * Copy for the problem details quoting reports when it could not price a shipment.
 *
 * They only ever arrive alongside SERVICE-UNAVAILABLE, and give the reason that code cannot.
 *
 * The registry shape and the fallback chain follow how the checkout app handles the same codes. The key
 * names deliberately diverge: the two apps translate in separate namespaces so matching them buys no
 * reuse, checkout needs a title/body pair where this row has one sentence, and the codes here are
 * normalised to SmartSend's own recipient/sender vocabulary.
 */

/**
 * Quoting names the same things differently to SmartSend, so its words are translated here at the boundary.
 * Everything downstream - the copy, the translation keys, the comparisons against our own ADDRESS-RECIPIENT-*
 * codes - then speaks one vocabulary.
 *
 * Only where the two genuinely mean the same field. Quoting's `shopid` and `country` have no counterpart
 * among our codes and are left as they arrive.
 *
 * Whole segments only, so a field whose name merely contains one of these words cannot be rewritten.
 */
const HouseTerms: Record<string, string> = {
  DESTINATION: 'RECIPIENT',
  ORIGIN: 'SENDER',
  CONTACTNAME: 'NAME',
  PROPERTY: 'ADDRESSLINE1'
};

/**
 * The stable identity of a fault, in the same hyphenated shape as the codes we raise ourselves - so
 * `errorcodes/validate/invalid-destination-postcode` reads as INVALID-RECIPIENT-POSTCODE. Key shaping is
 * left to the translation lookup, as it is for every other code in the app.
 *
 * Last path segment only, so it collapses the two forms quoting uses - errors arrive prefixed, warnings
 * bare - onto one code. Empty for a missing Type, so the caller can fall back to the reported prose.
 *
 * Beware one near-miss: quoting has its own `service-unavailable`, which then reads identically to our
 * SERVICE-UNAVAILABLE because it means the same thing. RedundantQuoteErrorCodes drops it.
 */
export const QuoteErrorCode = (type: string | undefined): string =>
  (type?.split('/').pop() ?? '')
    .toUpperCase()
    .split('-')
    .map((segment) => HouseTerms[segment] ?? segment)
    .join('-');

/**
 * QUOTE is load-bearing: quoting's `service-unavailable` reads the same as our own SERVICE-UNAVAILABLE,
 * so without the segment one entry would drive two unrelated sentences.
 *
 * `subCode` splits a Type that covers many faults into a key per fault - without it every one of
 * service-ineligible's ~33 situations would share one translation and none could name what was wrong.
 */
export const QuoteErrorTranslationKey = (code: string, subCode: string): string =>
  sanitiseKey(`ERROR_DESCRIPTION_QUOTE_${code}${subCode ? `-${subCode}` : ''}`);

/**
 * Our own English for a fault, or empty to fall through to the API's `Detail`.
 *
 * Seeded only with the codes a SmartSend quote can raise, and worded in this app's voice rather than
 * checkout's. New keys are not created automatically, so what is written here is what renders until a
 * translation exists for the key.
 *
 * SERVICE-INELIGIBLE and INVALID-REQUIREMENT are omitted on purpose, and pinned as such by tests: each
 * collapses dozens of situations under one Type, so only the reported `Detail` says which.
 */
export const QuoteErrorDefault = (code: string): string => {
  switch (code) {
    // Recipient address
    case 'INVALID-RECIPIENT-POSTCODE':
      return "We don't recognise your recipient's postcode. Please check it and try again.";
    case 'MISSING-RECIPIENT-POSTCODE':
      return "Your recipient's postcode is missing.";
    case 'INVALID-RECIPIENT-COUNTRY':
      return "Your recipient's country cannot be used with this service.";
    case 'MISSING-RECIPIENT-COUNTRY':
      return "Your recipient's country is missing.";
    case 'INVALID-RECIPIENT-COUNTY':
      return "Your recipient's county or state is not one this service recognises.";
    case 'MISSING-RECIPIENT-NAME':
      return "Your recipient's name is missing.";
    case 'MISSING-RECIPIENT-ADDRESSLINE1':
      return "Your recipient's address is missing a building name or number.";
    case 'INVALID-RECIPIENT-SHOPID':
      return 'The drop-off or pickup location chosen for your recipient is no longer available.';

    // Collection address
    case 'INVALID-SENDER-POSTCODE':
      return "We don't recognise your collection postcode. Please check it and try again.";
    case 'MISSING-SENDER-POSTCODE':
      return 'Your collection postcode is missing.';
    case 'INVALID-SENDER-COUNTRY':
      return 'Your collection country cannot be used with this service.';
    case 'MISSING-SENDER-COUNTRY':
      return 'Your collection country is missing.';
    case 'INVALID-SENDER-COUNTY':
      return 'Your collection county or state is not one this service recognises.';
    case 'MISSING-SENDER-NAME':
      return 'Your collection contact name is missing.';
    case 'MISSING-SENDER-ADDRESSLINE1':
      return 'Your collection address is missing a building name or number.';
    case 'INVALID-SENDER-SHOPID':
      return 'The drop-off location chosen for this shipment is no longer available.';

    // Parcels
    case 'MISSING-SHIPMENT-PARCELS':
      return 'This shipment has no parcels on it.';
    case 'INVALID-PARCEL-WEIGHT':
      return 'One of your parcels has a weight this service cannot use. Please check and update it.';
    case 'INVALID-PARCEL-VALUE':
      return 'One of your parcels has a declared value this service cannot use. Please check and update it.';
    case 'INVALID-PARCEL-QUANTITY':
      return 'This shipment has more parcels than this service can take.';
    case 'MISSING-PARCEL-CONTENTSUMMARY':
      return 'Please describe what is inside your parcel.';

    // Contents and customs
    case 'MISSING-PARCELCONTENT-DESCRIPTION':
      return 'One of the items in your parcel has no description.';
    case 'INVALID-PARCELCONTENT-QUANTITY':
      return 'One of the items in your parcel has a quantity we cannot use.';
    case 'INVALID-PARCELCONTENT-VALUE':
      return 'One of the items in your parcel has a value we cannot use.';
    case 'INVALID-PARCELCONTENT-WEIGHT':
      return 'One of the items in your parcel has a weight we cannot use.';
    case 'INVALID-PARCELCONTENT-COUNTRYOFMANUFACTURE':
      return 'One of the items in your parcel has a country of manufacture we cannot use.';
    case 'INVALID-PARCELCONTENT-TARIFFCODE':
      return 'One of the items in your parcel has a tariff code this service will not accept.';
    case 'MISSING-CUSTOMS-EXPORTREASON':
      return 'Please choose a reason for export on your customs declaration.';

    // Service
    case 'MISSING-SERVICE':
      return 'No service was chosen for this shipment.';
    case 'ROUTING-UNAVAILABLE':
      return 'This service cannot carry a shipment between these two addresses.';
    case 'COLLECTION-DATE-UNAVAILABLE':
      return 'This service cannot collect on the date you chose. Please pick a different day.';
    case 'EXTRA-UNAVAILABLE':
      return 'One of the extras on this shipment is not available with this service.';
    case 'CONFLICTING-EXTRAS':
      return 'Two of the extras on this shipment cannot be used together.';
  }

  return '';
};

/**
 * One quoting problem detail as a sentence: translation, then our copy, then the ineligibility copy, then
 * `Detail` (which carries the specifics, and where we have no copy is the only place they exist), then
 * `Title`. Empty when the issue carries no words at all.
 *
 * The lookup passes a BLANK default and picks the fallback itself rather than handing it to i18next, so
 * nothing depends on how missing-key creation is configured, and a key that exists but is EMPTY still
 * falls through to real copy. Safe because i18next counts `defaultValue: ''` as a default and treats ''
 * as a valid lookup, so an unresolvable key returns '' rather than the key name.
 */
export const QuoteErrorMessage = (
  t: TFunction<string, undefined>,
  error: QuoteError | undefined,
  code: string,
  subCode: string
): string => {
  // No code means no key to look anything up by, so the API's own words go out untranslated.
  const translated = code ? t(QuoteErrorTranslationKey(code, subCode), { defaultValue: '' }) : '';

  return (
    translated || QuoteErrorDefault(code) || ServiceIneligibilityMessage(subCode) || error?.Detail || error?.Title || ''
  );
};

/**
 * Codes that say no more than the headline they would replace: an item only reaches this row with no quote
 * via SERVICE-UNAVAILABLE, so being told the service was unavailable swaps one dead end for another.
 */
const RedundantQuoteErrorCodes = ['SERVICE-UNAVAILABLE', 'QUOTE-UNAVAILABLE'];

const AddressSides = ['RECIPIENT', 'SENDER'];

/**
 * Address fields that appear in both vocabularies, HouseTerms having already reconciled the names. An
 * unlisted field shows its sentence rather than being suppressed - the safe direction to be wrong in.
 */
const AddressFields = ['POSTCODE', 'COUNTY', 'NAME', 'ADDRESSLINE1'];

/** Whole segments, so NAME cannot match a future ORGANISATIONNAME and suppress a different field. */
const HasSegment = (code: string, segment: string) => code.split('-').includes(segment);

/**
 * Whether a quoting fault is about the same address field one of our own codes already reports, so the
 * row does not say "postcode is not in the correct format" and "we don't recognise your postcode" together.
 *
 * Compared on side and field rather than by mapping whole codes, which reverse the word order between the
 * two vocabularies - so this holds for a fault this build has never seen. Both parts read the same in
 * either code by the time they get here, QuoteErrorCode having reconciled the names.
 */
export const QuoteErrorIsCovered = (code: string, internalCode: string | undefined): boolean => {
  if (!code || !internalCode?.startsWith('ADDRESS-')) {
    return false;
  }

  const matches = (segment: string) => HasSegment(code, segment) && HasSegment(internalCode, segment);

  return AddressSides.some(matches) && AddressFields.some(matches);
};

/**
 * Our own resolution-only codes, so ErrorCodeButtonText supplies the label and ErrorCodeResolutionAction
 * the panel - as they already do for ITEM-WEIGHT-REVIEW. Otherwise a quoting fault inherits
 * SERVICE-UNAVAILABLE's "Change Service / Change Parcel Dimensions", neither of which helps when the
 * postcode is the problem.
 *
 * Matched on the recipient / sender segment rather than whole codes, so an address field this build has
 * never seen still lands in the right panel. Shop-id is checked first: it names a side but is fixed by
 * choosing a different drop-off. Empty means the row keeps its existing links.
 */
export const QuoteErrorResolutions = (code: string, subCode: string): string[] => {
  if (!code) {
    return [];
  }

  if (code === 'SERVICE-INELIGIBLE') {
    return ServiceIneligibilityResolutions(subCode);
  }

  if (code.includes('SHOPID')) {
    return ['SERVICE-CHANGE'];
  }

  if (code.includes('RECIPIENT')) {
    return ['ADDRESS-RECIPIENT-CHANGE'];
  }

  if (code.includes('SENDER')) {
    return ['ADDRESS-SENDER-CHANGE'];
  }

  if (code === 'INVALID-PARCEL-WEIGHT') {
    return ['ITEM-WEIGHT-REVIEW'];
  }

  return [];
};

/**
 * An issue with everything derived from it once: the sentence to render, and the codes the caller needs
 * to place it. Carried rather than re-derived, so the parse cannot disagree with itself between uses.
 */
export interface ResolvedQuoteError {
  error: QuoteError;
  code: string;
  subCode: string;
  message: string;
}

/**
 * The issues worth showing, in the order quoting sent them, resolved to their copy then deduped and capped.
 *
 * Deduped on the RESOLVED SENTENCE rather than on anything about the issue, because several distinct
 * issues legitimately collapse to one message: an oversized parcel breaches Length and Length+Girth
 * separately, and both read "This parcel is too big for this service". Comparing the rendered string is
 * the only check that cannot be fooled by a new code, a new sub-code or a translation that happens to
 * collapse two of them.
 *
 * A by-product is that the message is unique within the result, so it doubles as the React key.
 *
 * Resolved only up to `limit`: quoting sends one issue per breached limit per parcel, so a consignment
 * can arrive with several times more than the row will ever show.
 */
export const ResolveQuoteErrors = (
  t: TFunction<string, undefined>,
  errors: QuoteError[] | undefined,
  limit: number
): ResolvedQuoteError[] => {
  const resolved: ResolvedQuoteError[] = [];
  const seen = new Set<string>();

  for (const error of errors ?? []) {
    if (resolved.length === limit) {
      break;
    }

    const code = QuoteErrorCode(error?.Type);

    if (RedundantQuoteErrorCodes.includes(code)) {
      continue;
    }

    const subCode = ServiceIneligibilitySubCode(code, error);
    const message = QuoteErrorMessage(t, error, code, subCode);

    if (!message || seen.has(message)) {
      continue;
    }

    seen.add(message);
    resolved.push({ error, code, subCode, message });
  }

  return resolved;
};
